import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import React from "react"
import config from "../lib/config"

const background = reversed => (
  <div
    className="relative h-full text-lg max-w-prose mx-auto z-10 opacity-75 hidden md:block"
    aria-hidden="true"
  >
    {reversed ? (
      <svg
        className="absolute top-12 left-full transform -translate-y-1/2 translate-x-48"
        width={404}
        height={384}
        fill="none"
        viewBox="0 0 404 384"
      >
        <defs>
          <pattern
            id="74b3fd99-0a6f-4271-bef2-e80eeafdf357"
            x={0}
            y={0}
            width={20}
            height={20}
            patternUnits="userSpaceOnUse"
          >
            <rect
              x={0}
              y={0}
              width={4}
              height={4}
              className="text-gray-200"
              fill="currentColor"
            />
          </pattern>
        </defs>
        <rect
          width={404}
          height={384}
          fill="url(#74b3fd99-0a6f-4271-bef2-e80eeafdf357)"
        />
      </svg>
    ) : (
      <svg
        className="absolute top-1/2 right-full transform -translate-y-1/2 -translate-x-32"
        width={404}
        height={384}
        fill="none"
        viewBox="0 0 404 384"
      >
        <defs>
          <pattern
            id="f210dbf6-a58d-4871-961e-36d5016a0f49"
            x={0}
            y={0}
            width={20}
            height={20}
            patternUnits="userSpaceOnUse"
          >
            <rect
              x={0}
              y={0}
              width={4}
              height={4}
              className="text-gray-200"
              fill="currentColor"
            />
          </pattern>
        </defs>
        <rect
          width={404}
          height={384}
          fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)"
        />
      </svg>
    )}
  </div>
)

export default function showcase() {
  const getShowcaseImageClassName = (landscape = true, centered = false) =>
    `transform origin-left shadow-md ${
      landscape ? "rounded-xl w-full h-auto" : "h-auto w-72"
    } ${centered ? "" : "lg:absolute lg:left-0"}`
  const showcaseSection = (
    image,
    content,
    withMargin = true,
    reversed = false,
    imageCentered = false
  ) => {
    const { title, description, body } = content
    return (
      <div className={`relative ${withMargin ? "mt-8" : ""}`}>
        {background(reversed)}
        <div className="lg:mx-auto lg:max-w-7xl px-8 xl:px-0 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-16 relative z-20">
          <div
            className={`max-w-xl mx-auto lg:py-16 lg:max-w-none lg:mx-0 ${
              reversed ? "lg:col-start-2" : ""
            }`}
          >
            <div>
              <div>
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900">
                  {title}
                </h1>
              </div>
              <div className="mt-6">
                <h2 className="text-2xl font-medium tracking-tight text-gray-500">
                  {description}
                </h2>
                <p className="mt-4 text-lg text-gray-500">{body}</p>
                <div className="mt-6">
                  <Link
                    to="/request-a-demo"
                    className="transition-all inline-flex px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-default-600 hover:bg-default-700"
                  >
                    Request a Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 sm:mt-16 lg:mt-0">
            <div
              className={`w-full lg:pl-2 -mr-48 sm:pl-4 md:-mr-16 lg:px-0 lg:m-0 lg:relative lg:h-full flex lg:items-center ${
                imageCentered ? "justify-center" : ""
              }`}
            >
              {image}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative py-16 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-gray-100"
      />
      {showcaseSection(
        <StaticImage
          className={getShowcaseImageClassName()}
          src="../images/demo-1.png"
          alt="Demo Image"
          width={1000}
        />,
        {
          title: "Emergency Dashboard",
          description: `Manage emergencies and keep track of students in real-time`,
          body: `${config.siteTitle} keeps a live, real-time roll call of students
          to keep track of who is safe and who isn't. Authorized faculty
          members can update the roll call as they take attendance. Easily
          search, sort, and filter the roll call to find students fast and
          efficiently.`,
        },
        false
      )}
      {showcaseSection(
        <StaticImage
          className={getShowcaseImageClassName(false, true)}
          src="../images/demo-2.png"
          alt="Demo Image"
          style={{ borderRadius: "2.3rem" }}
          width={1000}
        />,
        {
          title: "Emergency Alerts & Messages",
          description: `Communicate with students fast and efficiently`,
          body: `Communication with students during an emergency is massively
          important, and with ${config.siteTitle} it's simple to notify
          students and parents of emergency updates through email, text
          message, and a phone call. Students can also be allowed to set
          their own statuses on the live roll call, improving the
          efficiency of the emergency response process.`,
        },
        true,
        true,
        true
      )}
      {showcaseSection(
        <StaticImage
          className={getShowcaseImageClassName()}
          src="../images/demo-3-v2.png"
          alt="Demo Image"
          width={1000}
        />,
        {
          title: "Custom Emergency Scenarios",
          description: `Create consistent scenarios that match your needs`,
          body: `Responding to specific types of emergencies fast can vastly improve
          the emergency response process. ${config.siteTitle} makes it easy to create
          customize notification messages and other options for particular emergency
          scenarios, for example a Fire Drill or Lockdown. This also makes it super simple
          to distinguish between a drill and a real emergency, and run more efficient
          and realistic drills as well.`,
        },
        true,
        false
      )}
      {showcaseSection(
        <StaticImage
          className={getShowcaseImageClassName(false, true)}
          src="../images/demo-4-v2.png"
          alt="Demo Image"
          style={{ borderRadius: "2.3rem" }}
          width={1256}
        />,
        {
          title: "Use It On Every Device",
          description: `SchoolSafe works on phones, computers, and everything in-between`,
          body: `SchoolSafe's emergency dashboard and live attendance sheet are fully
          available on all of your devices — from your computer to your tablet to your
          mobile phone. Having an live attendance sheet fit right in your pocket is
          massively helpful during emergency situations, and provides an unparalleled
          level of convenience and simplicity to the emergency response process.`,
        },
        true,
        true,
        true
      )}
    </div>
  )
}
